var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { OGButton } from '@opengear/buttons';
import { useResourceSmartgroups } from '@/api/smf-resources/smartgroups';
import { createNewFilter } from '@/utils/filter';
import { SelectFilterField } from '../filter-components';
import { ResourceFilterQueryContent } from './resource-filter-query-content';
import { ResourceFilterActions } from './resource-filter-action-button';
import { canApplySelectedFilter } from './resource-filter-utils';
import { useResourceFilterContext } from './resource-filter-context';
/**
 * Resource filter popover content
 * Assumes that the user has view permissions for smartgroups
 */
export function ResourceFilterPopoverContent(_a) {
    var handlePopoverClose = _a.handlePopoverClose;
    var t = useTranslation().t;
    var _b = useResourceFilterContext(), filter = _b.filter, selectedFilter = _b.selectedFilter, setFilter = _b.setFilter, dispatchSelectedFilter = _b.dispatchSelectedFilter, permissions = _b.permissions, error = _b.error;
    var _c = useResourceSmartgroups(), data = _c.data, isLoading = _c.isLoading;
    var options = useMemo(function () {
        var _a;
        var initialOptions = (_a = data === null || data === void 0 ? void 0 : data.smartgroups) !== null && _a !== void 0 ? _a : [];
        initialOptions.push(createNewFilter(t('components.resources.filter.select.new')));
        return initialOptions;
    }, [data === null || data === void 0 ? void 0 : data.smartgroups, t]);
    var applyDisabled = useMemo(function () { return !canApplySelectedFilter(selectedFilter, filter) || error; }, [selectedFilter, filter, error]);
    var handleFilterChange = useCallback(function (_, value) {
        dispatchSelectedFilter({ type: 'set', payload: value });
    }, [dispatchSelectedFilter]);
    var handleApply = useCallback(function () {
        // Only apply filter if conditions are met
        if (canApplySelectedFilter(selectedFilter, filter)) {
            setFilter(selectedFilter);
        }
        handlePopoverClose();
    }, [setFilter, filter, handlePopoverClose, selectedFilter]);
    return (_jsxs(_Fragment, { children: [_jsxs(Box, __assign({ alignItems: "flex-end", display: "flex", flexDirection: "row", gap: 1, marginBottom: 2, width: "100%" }, { children: [_jsx(SelectFilterField, { disabled: isLoading, options: options, onChange: handleFilterChange, textFieldProps: {
                            autoFocus: true,
                            label: t('components.resources.filter.select.label'),
                            placeholder: t('components.resources.filter.button.label'),
                        }, value: selectedFilter }), permissions.hasSmartgroupsEditAccess && _jsx(ResourceFilterActions, { handlePopoverClose: handlePopoverClose })] })), _jsx(ResourceFilterQueryContent, {}), _jsx(Box, { flexGrow: 1, overflow: "auto" }), _jsx(Divider, { sx: { marginX: -2 } }), _jsxs(Box, __assign({ display: "flex", gap: 1, justifyContent: "end", marginTop: 2 }, { children: [_jsx(OGButton, __assign({ variant: "borderless", onClick: handlePopoverClose }, { children: t('global.buttons.cancel') })), _jsx(OGButton, __assign({ disabled: applyDisabled, onClick: handleApply }, { children: t('global.buttons.apply') }))] }))] }));
}
